// Prompt construction for the three advisors (spec §6, plan Task 17). Everything the model is allowed
// to know about the fight goes through here: the last events as the report feed phrased them, blue's
// common operating picture, blue's own units with the card ids each one may legally play right now, the
// control graphics, and the deck's costs. Nothing from ground truth ever enters a prompt - an advisor
// sees exactly what the player sees, so a proposal it makes is one the player could have made.
//
// The split between the two blocks is the whole design, and it is a caching decision as much as a
// prompting one (pitch 3, "a staff that argues about your plan"; independent review §B, "prompt caching
// inert - the cached block is the 120-word persona, the 2k-token facts are in `user`").
//
//   system  everything that does not change from one tick to the next: the persona's doctrine, the
//           grounding and target rules, the commander's intent and mission, the control graphics, the
//           task organization, the player's plan and reserve, the dials, and the deck. Marked
//           `cache_control: {type:'ephemeral'}` by the proxy, so after the first call of a run this
//           whole prefix is served from Anthropic's cache - `usage.cache_read_input_tokens` on the
//           audit row (`cachedTokens`) is the evidence that it is.
//   user    only what moved: the clock, the recent events, the reported contacts, own-unit states with
//           each unit's legal cards, the orders in flight, this advisor's own previous play and whether
//           the player executed it, and the two plays currently shown for the other advisors.
//
// The user block is compact JSON rather than prose: it makes "use only these facts" checkable (every id
// the model may cite or target appears in it) and gives a model far less room to drift than a paragraph
// would. The system block is prose, because it is a brief and a person should be able to read it.

import advisorsJson from '../../data/advisors.json';
import { card, CARDS, isLegal } from '../sim/deck';
import { UNIT_DEFS } from '../sim/scenario';
import { clockHM, gridRef, reportText, type ReportCtx } from '../ui/reports';
import { TICKS_PER_MIN } from '../sim/types';
import type {
  CardDef, CardId, Contact, Event, Graphic, Order, Target, Unit,
} from '../sim/types';
import type { Stance } from './schemas';
import type { PlayContext } from './templates';

export type Persona = 'maneuver' | 'fires' | 'recon';

export interface AdvisorPersona {
  id: Persona;
  /** Display name on the card face, e.g. "MANEUVER". */
  name: string;
  /** One or two letters for the coloured face. */
  initials: string;
  /** The doctrine line under the name. */
  doctrine: string;
  /** A CSS custom property reference from src/styles.css. */
  color: string;
  /** The persona's own system prompt (≤ 120 words); the shared grounding rule is appended at build time. */
  system: string;
}

const DATA = advisorsJson as { grounding: string; glossary: string; personas: AdvisorPersona[] };

/** The three advisors in rail order. */
export const ADVISOR_PERSONAS: AdvisorPersona[] = DATA.personas;
export const PERSONA_IDS: Persona[] = ADVISOR_PERSONAS.map((p) => p.id);
/** The grounding rule every advisor shares: only the given facts, cited ids, listed legal cards. */
export const GROUNDING_RULE = DATA.grounding;
/**
 * The doctrine glossary from `docs/research/03-military-wargaming-doctrine.md` §Glossary, in the terms
 * this simulation actually uses. It is real content - an advisor that knows what "culmination" and
 * "main effort" mean writes better rationales - and it is also the ballast that keeps the cacheable
 * prefix over the minimum (see `SYSTEM_TOKEN_FLOOR`).
 */
export const DOCTRINE_GLOSSARY = DATA.glossary;

/**
 * One word per persona, taken from its doctrine line: the thing that advisor is for.
 *
 * Used to tell two identical play names apart. Three advisors asked the same question about the same
 * fight will sometimes arrive at the same four words, and two cards reading "Screen The North Flank"
 * look like a bug rather than like agreement.
 */
export const DOCTRINE_WORD: Record<Persona, string> = {
  maneuver: 'Gap', // "Find the gap, go now."
  fires: 'Mass', // "Mass effects, preserve the force."
  recon: 'Find', // "Find first, then decide."
};

export function personaDef(id: Persona): AdvisorPersona {
  const p = ADVISOR_PERSONAS.find((x) => x.id === id);
  if (!p) throw new Error(`Unknown advisor persona: ${id}`);
  return p;
}

/**
 * The part of an advisor's prompt that is fixed for a whole run: the order the staff was given, the
 * force it was given, and the plan the player made with it. Built once by `src/state/advisors.ts` from
 * `buildOpord` and the store's plan, and byte-identical from one refresh to the next, which is what
 * makes the system block cacheable at all.
 */
export interface AdvisorBrief {
  /** The area of operations, by its display title ("NTC — Fort Irwin"). */
  aoTitle: string;
  /** The mission statement, one sentence, from the OPORD (`src/ui/opord.ts`). */
  mission: string;
  /** The commander's intent: purpose, key tasks, end state. */
  intent: { purpose: string; keyTasks: string[]; endState: string };
  /** The enemy and terrain paragraphs of the order, and the S-2's labelled estimate. */
  situation: { enemy: string; posture: string; assessment: string; terrain: string | null };
  /** Task organization: the ids the simulation will use, their callsigns and what they are. */
  taskOrg: { id: number; callsign: string; type: string }[];
  /** The player's plan as it stands at H-hour: one opening card per tasked unit. */
  plan: { unitId: number; card: CardId; target: Target }[];
  /** Units deliberately held back with no opening order at all. */
  reserve: number[];
}

/**
 * What an advisor is shown. Extends the template play context (so the same object drives the fallback
 * path) with the orders already in flight, which tell the model which units are busy, the phase line
 * for a little situational framing, and the run brief above.
 */
export interface AdvisorContext extends PlayContext {
  orders: Order[];
  phase: string;
  /** The stable half of the prompt; null before the scenario is fitted (and in the older tests). */
  brief?: AdvisorBrief | null;
}

/** One advisor's own last answer, and what the player did with it. */
export interface PreviousPlay {
  name: string;
  rationale: string;
  stance?: Stance;
  orders: { unitId: number; card: CardId; target: Target }[];
  /** True when an order carrying this play's id reached the order book - the player clicked. */
  executed: boolean;
  /** Sim minutes since the play was built. */
  ageMin: number;
  /** It was a template, not a model answer; saying so stops "as I said before" about words it never said. */
  fromTemplate: boolean;
}

/** The memory half of the user block: this advisor's last play, and what the other two are saying now. */
export interface AdvisorMemory {
  previous: PreviousPlay | null;
  others: { persona: Persona; name: string; rationale: string }[];
}

/**
 * How much of the event log an advisor sees.
 *
 * The review found the advisors reasoning from twenty events, which in a busy minute is under two
 * minutes of fight. The window is now whichever is larger of the last 60 events and the last 12 sim
 * minutes, plus every significant event since H-hour however old - a platoon breaking at H+20 is still
 * the reason the flank is open at H+90 - capped so the prompt cannot run away in a heavy fight.
 */
export const PROMPT_EVENT_COUNT = 60;
export const PROMPT_EVENT_MINUTES = 12;
export const PROMPT_EVENT_CAP = 120;

/** Cards an advisor is never offered: free text has no gesture, and cancel is the player's own undo. */
const EXCLUDED_CARDS: ReadonlySet<CardId> = new Set<CardId>(['frago', 'cancel']);

/** Order statuses that mean the card is out on the net or being acted on, not finished. */
const PENDING_STATUSES = new Set(['queued', 'transmitting', 'received', 'executing']);

/**
 * A target that satisfies the card's target *stage* only, so `isLegal` reports what can be known before
 * a target is picked: the unit's type, liveness and airframe state. Mirrors `probeTarget` in the order
 * deck (src/ui/deck/Deck.tsx) - the advisors and the deck must agree on what is greyed out.
 */
function probeTarget(def: CardDef, unit: Unit, ctx: AdvisorContext): Target {
  switch (def.targetKind) {
    case 'point': return { kind: 'point', cell: ctx.grid.cellOfCm(unit.x, unit.y) };
    case 'graphic': return { kind: 'graphic', graphicId: ctx.graphics[0]?.id };
    case 'unit': return { kind: 'unit', unitId: unit.id };
    case 'text': return { kind: 'text', text: 'probe' };
    case 'contact': return { kind: 'contact' };
    case 'none': return { kind: 'none' };
  }
}

/** The card ids `unit` could legally play right now, on the same probe the order deck greys cards with. */
export function legalCardIds(unit: Unit, ctx: AdvisorContext): CardId[] {
  const out: CardId[] = [];
  for (const def of CARDS) {
    if (EXCLUDED_CARDS.has(def.id)) continue;
    if (isLegal(def.id, unit, probeTarget(def, unit, ctx), ctx.grid, ctx.graphics, ctx.units).ok) out.push(def.id);
  }
  return out;
}

function reportCtx(ctx: AdvisorContext): ReportCtx {
  return {
    units: new Map(ctx.units.map((u) => [u.id, u])),
    contacts: new Map(ctx.contacts.map((c) => [c.enemyId, c])),
    grid: ctx.grid,
    tick: ctx.t,
  };
}

/**
 * The events this prompt carries, oldest first: the wider of the two recency windows, plus every
 * significant event since H-hour, capped. When the cap bites it takes the oldest insignificant entries
 * first, so a full log loses routine traffic before it loses a platoon breaking.
 */
export function promptEvents(ctx: AdvisorContext): Event[] {
  const log = ctx.recentEvents.filter((e) => e.side === 0 && e.t <= ctx.t);
  if (log.length === 0) return [];

  const cutoff = ctx.t - PROMPT_EVENT_MINUTES * TICKS_PER_MIN;
  const byMinutes = log.filter((e) => e.t >= cutoff);
  const byCount = log.slice(-PROMPT_EVENT_COUNT);
  const recent = byMinutes.length >= byCount.length ? byMinutes : byCount;

  const ids = new Set(recent.map((e) => e.id));
  const olderSignificant = log.filter((e) => e.significant && !ids.has(e.id));
  let chosen = [...olderSignificant, ...recent];

  if (chosen.length > PROMPT_EVENT_CAP) {
    // Oldest routine traffic goes first: the significant log is what the argument is actually about.
    // If dropping all of it is still not enough, the newest of what is left wins.
    let toDrop = chosen.length - PROMPT_EVENT_CAP;
    const kept: Event[] = [];
    for (const e of chosen) {
      if (!e.significant && toDrop > 0) { toDrop--; continue; }
      kept.push(e);
    }
    chosen = kept.length > PROMPT_EVENT_CAP ? kept.slice(kept.length - PROMPT_EVENT_CAP) : kept;
  }
  return chosen.sort((a, b) => a.id - b.id);
}

function eventFacts(ctx: AdvisorContext): { id: number; t: string; kind: string; text: string }[] {
  const rc = reportCtx(ctx);
  const out: { id: number; t: string; kind: string; text: string }[] = [];
  for (const ev of promptEvents(ctx)) {
    const report = reportText(ev, rc);
    if (!report) continue; // red's own events and net bookkeeping never reach an advisor
    out.push({ id: ev.id, t: clockHM(ev.t), kind: ev.kind, text: report.text });
  }
  return out;
}

function contactFacts(ctx: AdvisorContext): Record<string, unknown>[] {
  return ctx.contacts.map((c: Contact) => ({
    enemyId: c.enemyId,
    grid: gridRef(ctx.grid.cellOfCm(c.x, c.y), ctx.grid),
    cell: ctx.grid.cellOfCm(c.x, c.y),
    typeConf: c.typeConf,
    ageMin: Math.round(Math.max(0, (ctx.t - c.lastSeen) / TICKS_PER_MIN)),
  }));
}

/** The card still on its way to this unit, if any - an advisor should not re-task a unit mid-order. */
function pendingCard(ctx: AdvisorContext, unitId: number): CardId | undefined {
  const o = ctx.orders.find(
    (x) => x.unitId === unitId && x.side === 0 && PENDING_STATUSES.has(x.status),
  );
  return o?.card;
}

/**
 * Every blue order still in flight, whoever proposed it. The review's advisors could not see what the
 * player had just done, so they proposed it again; this is the list that stops that, and the `play`
 * field says whether a card came from the player's own deck or from an advisor's bundle.
 */
function pendingOrderFacts(ctx: AdvisorContext): Record<string, unknown>[] {
  return ctx.orders
    .filter((o) => o.side === 0 && PENDING_STATUSES.has(o.status))
    .map((o) => ({
      unitId: o.unitId,
      card: o.card,
      status: o.status,
      issued: clockHM(o.issuedAt),
      ...(o.playId ? { play: o.playId } : {}),
    }));
}

function unitFacts(ctx: AdvisorContext): Record<string, unknown>[] {
  return ctx.units
    .filter((u) => u.side === 0 && u.alive)
    .map((u) => {
      const cell = ctx.grid.cellOfCm(u.x, u.y);
      const pending = pendingCard(ctx, u.id);
      return {
        id: u.id,
        callsign: u.callsign,
        type: UNIT_DEFS[u.type].label,
        strength: Math.round(u.strength),
        fuel: Math.round(u.fuel),
        ammo: Math.round(u.ammo),
        posture: u.posture,
        grid: gridRef(cell, ctx.grid),
        cell,
        inContact: u.inContact,
        ...(pending ? { pending } : {}),
        // `cardId:targetKind`, exactly as the FRAGO prompt lists them (src/ai/feedPrompts.ts
        // `unitLine`). The card id alone does not say what a target may be, and the model read
        // "attack PL RED" as a graphic target for a point card - `validatePlay` then dropped the order
        // and the card said "dropped: Launch UAS needs a point on the map". Send only the id in `card`.
        legal: legalCardIds(u, ctx).map((id) => `${id}:${card(id).targetKind}`).join(','),
      };
    });
}

// ---- the stable half ---------------------------------------------------------------------------------

function targetPhrase(t: Target, ctx: AdvisorContext): string {
  switch (t.kind) {
    case 'point': return t.cell === undefined ? 'a point' : `cell ${t.cell} (${gridRef(t.cell, ctx.grid)})`;
    case 'graphic': return ctx.graphics.find((g) => g.id === t.graphicId)?.name ?? `graphic ${t.graphicId}`;
    case 'unit': return `unit ${t.unitId}`;
    case 'contact': return 'a reported contact';
    case 'text': return 'free text';
    case 'none': return 'no target';
  }
}

/** The control graphics as lines, with the cell a `:point` card would aim at for each. */
function graphicLines(ctx: AdvisorContext): string[] {
  return ctx.graphics.map((g: Graphic) => {
    const cell = ctx.grid.cellOfCm(g.pts[0].x, g.pts[0].y);
    const objective = g.id === ctx.objectiveId ? ' — THE OBJECTIVE' : '';
    return `  ${g.id} (${g.kind}) "${g.name}" at cell ${cell}, grid ${gridRef(cell, ctx.grid)}${objective}`;
  });
}

/** The deck as lines: what each card wants aimed at it and what it costs on the net and in execution. */
function deckLines(): string[] {
  return CARDS
    .filter((c) => !EXCLUDED_CARDS.has(c.id))
    .map((c) => `  ${c.id}:${c.targetKind} — ${c.label} (${c.family}); issue ${c.issueSec} s, execute ${c.execSec} s`);
}

function taskOrgLines(ctx: AdvisorContext): string[] {
  const brief = ctx.brief;
  const org = brief?.taskOrg ?? ctx.units
    .filter((u) => u.side === 0)
    .map((u) => ({ id: u.id, callsign: u.callsign, type: UNIT_DEFS[u.type].label }));
  return org.map((u) => `  ${u.id} ${u.callsign} — ${u.type}`);
}

function planLines(ctx: AdvisorContext): string[] {
  const brief = ctx.brief;
  if (!brief) return [];
  const lines = brief.plan.map((o) => {
    const callsign = brief.taskOrg.find((u) => u.id === o.unitId)?.callsign ?? `unit ${o.unitId}`;
    return `  ${callsign} (${o.unitId}): ${o.card} → ${targetPhrase(o.target, ctx)}`;
  });
  if (brief.reserve.length > 0) {
    const held = brief.reserve
      .map((id) => brief.taskOrg.find((u) => u.id === id)?.callsign ?? `unit ${id}`)
      .join(', ');
    lines.push(`  In reserve, no opening order: ${held}`);
  }
  return lines.length > 0 ? lines : ['  The commander tasked nothing at H-hour.'];
}

function dialLines(ctx: AdvisorContext): string[] {
  const d = ctx.dials;
  return [
    `  Command and control: ${d.c2} (how much delay an order pays between headquarters and the unit).`,
    `  Electronic warfare: ${d.ew} (jamming against the command net and against unmanned aircraft).`,
    `  Time of day: ${d.tod} (sensor range and detection quality).`,
    `  Enemy posture: ${d.red}.`,
  ];
}

/**
 * The block that does not change for a whole run, and therefore the block that is worth caching.
 *
 * Order matters: the persona's own doctrine first (it is what the model is being asked to be), then the
 * rules that override it, then the brief. A prefix cache is a byte match, so nothing built from the
 * clock, a tick, a snapshot or a random number may appear anywhere in here.
 */
export function buildAdvisorSystem(persona: Persona, ctx: AdvisorContext): string {
  const def = personaDef(persona);
  const brief = ctx.brief;
  const parts: string[] = [def.system, GROUNDING_RULE, TARGET_RULE, STANCE_RULE];

  if (brief) {
    parts.push([
      'THE ORDER YOU ARE ADVISING UNDER',
      `Area of operations: ${brief.aoTitle}.`,
      `Mission: ${brief.mission}`,
      `Commander's intent — purpose: ${brief.intent.purpose}`,
      `Key tasks: ${brief.intent.keyTasks.join(' ')}`,
      `End state: ${brief.intent.endState}`,
      `Enemy: ${brief.situation.enemy} ${brief.situation.posture}`,
      `S-2 assessment (an estimate, not a report): ${brief.situation.assessment}`,
      ...(brief.situation.terrain ? [brief.situation.terrain] : []),
    ].join('\n'));
  }

  parts.push(['TASK ORGANIZATION (the ids you may task; a unit is legal only if it is also in units[])',
    ...taskOrgLines(ctx)].join('\n'));
  parts.push(['CONTROL GRAPHICS', ...graphicLines(ctx)].join('\n'));

  if (brief) {
    parts.push([
      'THE COMMANDER\'S PLAN AS EXECUTED AT H-HOUR',
      'This is what the player actually ordered before the fight started. Your play either carries it',
      'forward, adjusts it, or argues against it — say which in `stance` and why in `rationale`.',
      ...planLines(ctx),
    ].join('\n'));
  }

  parts.push(['CONDITIONS FOR THIS RUN (the instructor\'s dials)', ...dialLines(ctx)].join('\n'));
  parts.push(['THE DECK — every card that exists, what it takes as a target, and what it costs',
    ...deckLines()].join('\n'));

  const system = parts.join('\n\n');
  return estimatedTokens(system) >= SYSTEM_TOKEN_FLOOR ? system : `${system}\n\n${DOCTRINE_GLOSSARY}`;
}

/**
 * The minimum cacheable prefix, with headroom.
 *
 * Anthropic will not create a cache entry for a prefix under a per-model floor, and it fails silently:
 * the request succeeds, `cache_read_input_tokens` is zero forever, and nothing says why. The floor is
 * 1024 tokens for Sonnet-class models (verified against the Claude API reference, 2026-09-21; it is
 * 512 on Opus 5 and 4096 on Haiku 4.5 — not monotonic, so it cannot be guessed). The advisors run on
 * `claude-sonnet-5`, so 1024 is the number that matters, and the floor below carries enough headroom to
 * survive a short AO with few graphics; under it, the doctrine glossary is appended as real ballast.
 */
export const SYSTEM_TOKEN_FLOOR = 1200;

/** The usual rule of thumb, and the same one the AI audit's "prompt ≈tok" column uses. */
export function estimatedTokens(text: string): number {
  return Math.round(text.length / 4);
}

// ---- the volatile half -------------------------------------------------------------------------------

function memoryFacts(memory: AdvisorMemory | undefined, persona: Persona): Record<string, unknown> {
  const previous = memory?.previous;
  const others = (memory?.others ?? []).filter((o) => o.persona !== persona);
  return {
    your_previous_play: previous
      ? {
        name: previous.name,
        rationale: previous.rationale,
        stance: previous.stance,
        orders: previous.orders,
        minutes_ago: Math.round(previous.ageMin),
        commander_executed_it: previous.executed,
        was_a_template: previous.fromTemplate,
      }
      : null,
    other_advisors_now_proposing: others.map((o) => ({
      advisor: o.persona, name: o.name, rationale: o.rationale,
    })),
  };
}

/**
 * The system/user pair for one advisor. `system` is the run brief (stable, cached); `user` is the facts
 * that moved, and nothing but the facts.
 */
export function buildAdvisorPrompt(
  persona: Persona, ctx: AdvisorContext, memory?: AdvisorMemory,
): { system: string; user: string } {
  const def = personaDef(persona);
  const objective = ctx.graphics.find((g) => g.id === ctx.objectiveId);

  const facts = {
    clock: clockHM(ctx.t),
    phase: ctx.phase,
    objective: objective ? { id: objective.id, name: objective.name } : null,
    events: eventFacts(ctx),
    contacts: contactFacts(ctx),
    units: unitFacts(ctx),
    pendingOrders: pendingOrderFacts(ctx),
    ...memoryFacts(memory, persona),
  };

  return {
    system: buildAdvisorSystem(persona, ctx),
    user:
      'Situation as it stands now (the whole of what you know that the order did not already tell you):\n' +
      `${JSON.stringify(facts)}\n\n` +
      `Propose ${def.name}'s play now. Cite the event ids you used. ` +
      'Every order must name a unit id from units[] and a card id from that unit\'s own legal list, ' +
      'with a target of exactly the kind printed after that card id. ' +
      'Set `stance` against your previous play, and name `disagreesWith` only if your play cannot be ' +
      'run alongside the play shown for that advisor.',
  };
}

/**
 * How a target is shaped, per card — the rule the FRAGO prompt has always carried and the advisor
 * prompt never did. That omission is the root cause of "dropped: Launch UAS needs a point on the map":
 * the advisors kept naming a control graphic as the target of a point card, and every such order was
 * thrown away between the model and the card face.
 */
const TARGET_RULE = [
  'Each entry in a unit\'s legal list is written `cardId:targetKind`. Put only the `cardId` in `card`,',
  'and give `target` exactly that kind: `:point` takes {"kind":"point","cell":N}, `:graphic` takes',
  '{"kind":"graphic","graphicId":"..."}, `:unit` takes {"kind":"unit","unitId":N}, `:contact` takes',
  '{"kind":"contact","unitId":N} naming a reported enemy id, and `:none` takes {"kind":"none"}.',
  'Naming a control graphic does not make the target kind "graphic": a graphic is also a place, so for',
  'a `:point` card (attack, move, defend, withdraw, fire missions, smoke, suppress, launch UAS) use the',
  '`cell` printed next to that graphic in the control graphics list, and only ever send `graphicId` for',
  'a `:graphic` card (screen, recon, observe). An order whose target kind does not match its card is',
  'dropped before the commander sees it.',
  'Name the play in Title Case, at most four words.',
].join(' ');

/**
 * Memory and argument: the two things the review found missing ("advisors get 20 events, no intent, no
 * plan, no memory of their previous play"). An advisor that cannot remember what it said cannot be
 * argued with, and three advisors who never mention each other are three cards, not a staff.
 */
const STANCE_RULE = [
  'You are shown your own previous play, whether the commander executed it, and the plays the other two',
  'advisors are showing right now. Use them.',
  'Set `stance` to "continue" when you are standing by the play you already gave (say so in the',
  'rationale — "as before", "still the same call"), "change" when the situation has moved and you are',
  'now advising something else (say what changed), or "urgent" when acting this minute is the whole',
  'point and waiting for the next refresh loses the chance.',
  'Set `disagreesWith` to another advisor\'s id (maneuver, fires or recon) only when your play and the',
  'play shown for that advisor cannot both be executed — the same platoon tasked two ways, movement',
  'across ground nobody has suppressed, committing the reserve against holding it. Say in one clause',
  'why, in the rationale, because the commander is shown your reason on both cards. If the plays merely',
  'differ in emphasis, leave `disagreesWith` out: a staff that disagrees with everything is noise.',
].join(' ');

/** Every event id an advisor was shown, for `validateCitations`. */
export function citableEventIds(ctx: AdvisorContext): Set<number> {
  return new Set(eventFacts(ctx).map((e) => e.id));
}

/** Re-exported for the advisor cards: the deck cost of one card id. */
export function cardCost(id: CardId): { issueSec: number; execSec: number } {
  const def = CARDS.find((c) => c.id === id);
  return { issueSec: def?.issueSec ?? 0, execSec: def?.execSec ?? 0 };
}

export type { Event };
